import {
  ApprovalMode,
  type CapabilityBucket,
  type CapabilityGroup,
  type CapabilityGroupTool,
  CapabilityMode,
  type CapabilitySection,
  CapabilitySectionKind,
  type FloraCapabilitiesResponse,
  type FloraUserPreferences,
  type StandaloneCapability,
  ToolLoadingMode,
} from '../../../../api/agents-client/agent-client.type';
import type { ApprovalAggregate, ApprovalGroupState, CustomizeDraft, EnableGroupState } from './agent-customize.type';

/**
 * Invokes `callback` for every enable-mode tool in the catalog — the read/write
 * tools of every bucket capability (buckets are always enable-mode) plus the
 * read/write tools of any standalone section whose `mode` is `Enable` (e.g. the
 * Office / Word & Excel section). Approval-mode standalone sections are skipped.
 *
 * @param {FloraCapabilitiesResponse} catalog - The capability catalog
 * @param {(tool: CapabilityGroupTool) => void} callback - Invoked per enable-mode tool
 * @returns {void}
 */
function forEachEnableTool(catalog: FloraCapabilitiesResponse, callback: (tool: CapabilityGroupTool) => void): void {
  for (const section of catalog.sections) {
    if (section.kind === CapabilitySectionKind.Bucket) {
      for (const capability of section.capabilities) {
        for (const tool of capability.read) callback(tool);
        for (const tool of capability.write) callback(tool);
      }
    } else if (section.mode === CapabilityMode.Enable) {
      for (const tool of section.read) callback(tool);
      for (const tool of section.write) callback(tool);
    }
  }
}

/**
 * Invokes `callback` for every approval-mode write tool in the catalog — the
 * write tools of standalone sections whose `mode` is `Approval`. Read tools of
 * those sections are always-on and carry no approval control, and enable-mode
 * standalone sections (e.g. Office) are skipped since they carry no approvals.
 *
 * @param {FloraCapabilitiesResponse} catalog - The capability catalog
 * @param {(tool: CapabilityGroupTool) => void} callback - Invoked per approval-mode write tool
 * @returns {void}
 */
function forEachApprovalWriteTool(
  catalog: FloraCapabilitiesResponse,
  callback: (tool: CapabilityGroupTool) => void,
): void {
  for (const section of catalog.sections) {
    if (section.kind !== CapabilitySectionKind.Capability) continue;
    if (section.mode !== CapabilityMode.Approval) continue;
    for (const tool of section.write) callback(tool);
  }
}

/**
 * Resolves the catalog + a user's sparse saved preferences into a fully
 * populated draft. The catalog is authoritative for the set of tools; stored
 * settings are sparse overrides layered over per-tool defaults.
 *
 * Resolution rules (each applied independently so a missing key defaults on its
 * own): a required tool is always enabled; an enable-mode tool absent from
 * `disabled_tools` is enabled; an approval-mode write tool absent from
 * `tool_approvals` falls back to its catalog `approval_default`; a missing
 * `tool_loading` is `ondemand`; a missing `default_approval_mode` is `ask`; a
 * missing `reference_chat_history` is on.
 * Stale names in the stored settings that aren't in the catalog are ignored
 * because only catalog tools are iterated.
 *
 * @param {FloraCapabilitiesResponse} catalog - The available capability catalog
 * @param {FloraUserPreferences | null | undefined} preferences - The user's saved (possibly empty) settings
 * @returns {CustomizeDraft} A fully-defaulted, catalog-validated draft
 */
function mergeSettingsWithCatalog(
  catalog: FloraCapabilitiesResponse,
  preferences: FloraUserPreferences | null | undefined,
): CustomizeDraft {
  const disabledSet = new Set(preferences?.disabled_tools ?? []);
  const approvalOverrides = preferences?.tool_approvals ?? {};

  const enabledTools: Record<string, boolean> = {};
  forEachEnableTool(catalog, (tool) => {
    enabledTools[tool.name] = tool.required ? true : !disabledSet.has(tool.name);
  });

  const approvals: Record<string, boolean> = {};
  forEachApprovalWriteTool(catalog, (tool) => {
    // Saved override wins; otherwise fall back to the tool's catalog default
    // (external write tools default to approval-required, others auto-approve).
    approvals[tool.name] =
      tool.name in approvalOverrides ? Boolean(approvalOverrides[tool.name]) : Boolean(tool.approval_default);
  });

  return {
    toolLoading: preferences?.tool_loading ?? catalog.tool_loading ?? ToolLoadingMode.OnDemand,
    approvalMode: preferences?.default_approval_mode ?? catalog.default_approval_mode ?? ApprovalMode.Ask,
    referenceChatHistory: preferences?.reference_chat_history ?? catalog.reference_chat_history ?? true,
    enabledTools,
    approvals,
  };
}

/**
 * Builds the pristine default draft for "Reset to defaults": every enable-mode
 * tool on, every approval-mode write tool restored to its catalog default via
 * `approval_default` (external write tools like Jira / save-custom-calculation
 * default to approval-required; internal ones auto-approve), on-demand
 * loading, Ask approval mode, and chat-history referencing on — independent of
 * the user's current saved state. This mirrors
 * `mergeSettingsWithCatalog(catalog, null)`.
 *
 * @param {FloraCapabilitiesResponse} catalog - The available capability catalog
 * @returns {CustomizeDraft} The default draft
 */
function getDefaultDraft(catalog: FloraCapabilitiesResponse): CustomizeDraft {
  const enabledTools: Record<string, boolean> = {};
  forEachEnableTool(catalog, (tool) => {
    enabledTools[tool.name] = true;
  });

  const approvals: Record<string, boolean> = {};
  forEachApprovalWriteTool(catalog, (tool) => {
    approvals[tool.name] = Boolean(tool.approval_default);
  });

  return {
    toolLoading: ToolLoadingMode.OnDemand,
    approvalMode: ApprovalMode.Ask,
    referenceChatHistory: true,
    enabledTools,
    approvals,
  };
}

/**
 * Computes the normalized preferences payload for a save from the current
 * draft. Only the Customize keys are returned; the backend shallow-merges
 * them over the rest of the stored preferences.
 *
 * `disabled_tools` lists only non-required enable-mode tools toggled OFF;
 * `tool_approvals` maps every approval-mode write tool to its current flag.
 *
 * @param {FloraCapabilitiesResponse} catalog - The catalog the draft was built from
 * @param {CustomizeDraft} draft - The current edited draft
 * @returns {FloraUserPreferences} The `{ tool_loading, default_approval_mode, reference_chat_history, disabled_tools, tool_approvals }` payload
 */
function computePreferencesPayload(catalog: FloraCapabilitiesResponse, draft: CustomizeDraft): FloraUserPreferences {
  const disabledTools: string[] = [];
  forEachEnableTool(catalog, (tool) => {
    if (!tool.required && draft.enabledTools[tool.name] === false) {
      disabledTools.push(tool.name);
    }
  });

  const toolApprovals: Record<string, boolean> = {};
  forEachApprovalWriteTool(catalog, (tool) => {
    toolApprovals[tool.name] = Boolean(draft.approvals[tool.name]);
  });

  return {
    tool_loading: draft.toolLoading,
    default_approval_mode: draft.approvalMode,
    reference_chat_history: draft.referenceChatHistory,
    disabled_tools: disabledTools,
    tool_approvals: toolApprovals,
  };
}

/**
 * Aggregates on/off counts for a list of enable-mode tools.
 *
 * @param {CapabilityGroupTool[]} tools - The tools to summarize
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {EnableGroupState} Counts, optional-tool names, and derived toggle state
 */
function getEnableGroupState(tools: CapabilityGroupTool[], enabledTools: Record<string, boolean>): EnableGroupState {
  const optionalNames = tools.filter((tool) => !tool.required).map((tool) => tool.name);
  const onCount = tools.filter((tool) => enabledTools[tool.name]).length;
  const hasOptional = optionalNames.length > 0;
  const allOn = !hasOptional || optionalNames.every((name) => enabledTools[name]);
  return { total: tools.length, onCount, optionalNames, allOn, hasOptional };
}

/**
 * Aggregates approval state for a list of approval-mode write tools.
 *
 * @param {CapabilityGroupTool[]} writeTools - The write tools to summarize
 * @param {Record<string, boolean>} approvals - Draft approval-required map
 * @returns {ApprovalGroupState} Counts and derived toggle state
 */
function getApprovalGroupState(
  writeTools: CapabilityGroupTool[],
  approvals: Record<string, boolean>,
): ApprovalGroupState {
  const names = writeTools.map((tool) => tool.name);
  const requiredCount = names.filter((name) => approvals[name]).length;
  const allRequired = names.length > 0 && requiredCount === names.length;
  return { total: names.length, requiredCount, allRequired, names };
}

/**
 * Reduces a capability's write tools to their collective approval posture for
 * the per-capability approval dropdown.
 *
 * @param {CapabilityGroupTool[]} writeTools - The capability's write tools
 * @param {Record<string, boolean>} approvals - Draft approval-required map
 * @returns {ApprovalAggregate} `allow` (none require approval), `approval` (all
 *   do), or `custom` (mixed). Empty write sets are treated as `allow`.
 */
function getApprovalAggregate(
  writeTools: CapabilityGroupTool[],
  approvals: Record<string, boolean>,
): ApprovalAggregate {
  if (writeTools.length === 0) return 'allow';
  const requiredCount = writeTools.filter((tool) => approvals[tool.name]).length;
  if (requiredCount === 0) return 'allow';
  if (requiredCount === writeTools.length) return 'approval';
  return 'custom';
}

/**
 * Aggregates the enable-state of a whole capability (its read + write tools).
 *
 * @param {CapabilityGroup} capability - The capability to summarize
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {EnableGroupState} The combined enable-group state
 */
function getCapabilityEnableState(
  capability: CapabilityGroup,
  enabledTools: Record<string, boolean>,
): EnableGroupState {
  return getEnableGroupState([...capability.read, ...capability.write], enabledTools);
}

/**
 * Aggregates the enable-state across every tool in a bucket. Backs the
 * bucket-wide toggle-all switch (all-on / has-optional / optional names).
 *
 * @param {CapabilityBucket} bucket - The bucket to summarize
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {EnableGroupState} Combined enable-group state across the bucket
 */
function getBucketState(bucket: CapabilityBucket, enabledTools: Record<string, boolean>): EnableGroupState {
  const allTools = bucket.capabilities.flatMap((capability) => [...capability.read, ...capability.write]);
  return getEnableGroupState(allTools, enabledTools);
}

/**
 * Whether a capability currently has at least one enabled tool.
 *
 * @param {CapabilityGroup} capability - The capability to inspect
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {boolean} True when any read or write tool is enabled (or required)
 */
function capabilityHasEnabledTool(capability: CapabilityGroup, enabledTools: Record<string, boolean>): boolean {
  return [...capability.read, ...capability.write].some((tool) => tool.required || enabledTools[tool.name] === true);
}

/**
 * Applies cross-capability locks within a bucket. A capability declaring
 * `enabled_with` stays locked on while any listed sibling capability has an
 * enabled tool: its tools are marked `required` (and forced `enabled`) so the
 * UI hides its toggle and keeps it out of the bucket-wide toggle-all. Once every
 * listed sibling is fully off, the capability unlocks and becomes toggleable
 * again. Mirrors the backend `_apply_capability_locks` guarantee.
 *
 * @param {CapabilityGroup[]} capabilities - The bucket's capabilities
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {CapabilityGroup[]} Capabilities with locked ones' tools forced on
 */
function applyCapabilityLocks(
  capabilities: CapabilityGroup[],
  enabledTools: Record<string, boolean>,
): CapabilityGroup[] {
  const enabledIds = new Set(
    capabilities.filter((capability) => capabilityHasEnabledTool(capability, enabledTools)).map((c) => c.id),
  );
  return capabilities.map((capability) => {
    const locked = capability.enabled_with?.some((siblingId) => enabledIds.has(siblingId)) ?? false;
    if (!locked) return capability;
    const lock = (tool: CapabilityGroupTool): CapabilityGroupTool => ({ ...tool, required: true, enabled: true });
    return { ...capability, read: capability.read.map(lock), write: capability.write.map(lock) };
  });
}

/**
 * Aggregates the enable-state across every tool in an enable-mode standalone
 * section (e.g. Office / Word & Excel). Backs the section-wide on/off toggle.
 *
 * @param {StandaloneCapability} section - The enable-mode standalone section
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {EnableGroupState} Combined enable-group state across the section
 */
function getStandaloneEnableState(
  section: StandaloneCapability,
  enabledTools: Record<string, boolean>,
): EnableGroupState {
  return getEnableGroupState([...section.read, ...section.write], enabledTools);
}

/**
 * Whether an enable-mode standalone section is fully disabled — it has optional
 * tools and every one of them is off. Backs the tool-list dimming: a
 * partially-enabled section is NOT off, so its still-enabled and always-on
 * required tools stay interactive rather than being dimmed to `pointer-events:
 * none`. A section with no optional tools (only always-on required ones) is
 * never off.
 *
 * @param {StandaloneCapability} section - The enable-mode standalone section
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {boolean} True only when the section has optional tools and all are off
 */
function isStandaloneSectionOff(section: StandaloneCapability, enabledTools: Record<string, boolean>): boolean {
  const { hasOptional, optionalNames } = getStandaloneEnableState(section, enabledTools);
  return hasOptional && optionalNames.every((name) => enabledTools[name] === false);
}

/**
 * Builds the summary line for an enable-mode standalone section header.
 *
 * @param {StandaloneCapability} section - The enable-mode standalone section
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {string} "Disabled" when nothing is on, else "N of M tools enabled"
 */
function getStandaloneEnableSummary(section: StandaloneCapability, enabledTools: Record<string, boolean>): string {
  const { total, onCount } = getStandaloneEnableState(section, enabledTools);
  return onCount === 0 ? 'Disabled' : `${onCount} of ${total} tools enabled`;
}

/**
 * Builds the summary line for an enable-mode capability row.
 *
 * @param {CapabilityGroup} capability - The capability
 * @param {Record<string, boolean>} enabledTools - Draft enable-state map
 * @returns {string} "Disabled" when nothing is on, else "N of M tools enabled"
 */
function getCapabilitySummary(capability: CapabilityGroup, enabledTools: Record<string, boolean>): string {
  const { total, onCount } = getCapabilityEnableState(capability, enabledTools);
  return onCount === 0 ? 'Disabled' : `${onCount} of ${total} tools enabled`;
}

/**
 * Builds the summary line for a standalone approval-mode capability section.
 *
 * @param {CapabilitySection} section - A standalone (approval) capability section
 * @param {Record<string, boolean>} approvals - Draft approval-required map
 * @returns {string} A description of the section's approval posture
 */
function getApprovalSectionSummary(
  section: Extract<CapabilitySection, { kind: CapabilitySectionKind.Capability }>,
  approvals: Record<string, boolean>,
): string {
  const { total, requiredCount } = getApprovalGroupState(section.write, approvals);
  if (total === 0) return 'Always available';
  if (requiredCount === 0) return 'Runs autonomously';
  return `${requiredCount} of ${total} ${total === 1 ? 'action' : 'actions'} need approval`;
}

export {
  mergeSettingsWithCatalog,
  getDefaultDraft,
  computePreferencesPayload,
  getEnableGroupState,
  getApprovalGroupState,
  getApprovalAggregate,
  getCapabilityEnableState,
  getBucketState,
  applyCapabilityLocks,
  getStandaloneEnableState,
  isStandaloneSectionOff,
  getCapabilitySummary,
  getStandaloneEnableSummary,
  getApprovalSectionSummary,
};
